import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button } from '../../../components/ui/Button';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '@lov/shared';

interface MobileBookingBarProps {
  basePrice: number;
  currency?: string;
  onBookNow: () => void;
  visible?: boolean;
}

const BarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible?: boolean }>(({ theme, visible }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
  transform: visible ? 'translateY(0)' : 'translateY(100%)',
  transition: 'transform 0.3s ease',
  // Safe area padding for iOS
  paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
  // Hide on desktop
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const PriceSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const PriceLabel = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
}));

const PriceRow = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  gap: 4,
});

const Price = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '1.25rem',
  fontWeight: 700,
  color: theme.palette.success.main,
}));

const PriceUnit = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

const BookButton = styled(Button)(({ theme }) => ({
  minWidth: 140,
  padding: '12px 24px',
  fontSize: '1rem',
  fontWeight: 600,
  borderRadius: 10,
  backgroundColor: theme.palette.text.primary,
  color: theme.palette.background.paper,
  transition: 'all 0.2s ease',
  boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, 0.25)}`,
  '&:hover': {
    backgroundColor: theme.palette.text.primary,
    transform: 'scale(1.02)',
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
}));

export function MobileBookingBar({
  basePrice,
  currency = 'INR',
  onBookNow,
  visible = true,
}: MobileBookingBarProps) {
  const { isEnabled: isBookNowEnabled } = useFeatureFlag(FEATURE_FLAGS.BOOK_NOW);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <BarContainer visible={visible}>
      <PriceSection>
        <PriceLabel>Starting from</PriceLabel>
        <PriceRow>
          <Price>{formatPrice(basePrice)}</Price>
          <PriceUnit>/ hour</PriceUnit>
        </PriceRow>
      </PriceSection>

      <BookButton onClick={isBookNowEnabled ? onBookNow : undefined} disabled={!isBookNowEnabled}>
        {isBookNowEnabled ? 'Book Now' : 'Coming Soon'}
      </BookButton>
    </BarContainer>
  );
}
